import React from 'react';

import { ButtonProps, ButtonWithStatus, WhiteButtonWithStatus } from '@components/Button';
import { useRequests } from '@context/RequestsContext';
import { useCurrentUser } from '@context/UsersContext';
import { RequestMutationAction, useJoinGroupRequestMutation } from '@db/Requests/Queries';
import { errorAlert } from '@helpers/Alert';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { RequestStatus } from '@types';

type JoinGroupButtonProps = {
  groupId: string;
  variant: 'large' | 'small';
} & Omit<ButtonProps, 'children'>;

export default function JoinGroupButton({ groupId, variant, ...props }: JoinGroupButtonProps) {
  const { userDoc } = useCurrentUser();
  const { pendingJoinGroupRequestGroupIds } = useRequests();
  const currentUserId = getCurrentUserId();
  const userGroupIds = userDoc?.groupIds;

  const isUserMember = (groupId: string, groupIds?: string[]) => {
    return (groupIds || []).includes(groupId);
  };

  const isUserRequested = (groupId: string, requestIds: Set<string>) => {
    return requestIds.has(groupId);
  };

  const [isLoading, setIsLoading] = React.useState(false);
  const [isMember, setIsMember] = React.useState<boolean>(isUserMember(groupId, userGroupIds));
  const [isRequested, setIsRequested] = React.useState<boolean>(
    isUserRequested(groupId, pendingJoinGroupRequestGroupIds)
  );
  const joinGroupMutation = useJoinGroupRequestMutation();

  React.useEffect(() => {
    if (isUserMember(groupId, userGroupIds)) {
      // Server/listener state can confirm membership after an optimistic local update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsMember(true);
    }
  }, [groupId, userGroupIds]);

  React.useEffect(() => {
    if (isUserRequested(groupId, pendingJoinGroupRequestGroupIds)) {
      // Server/listener state can confirm a pending request after an optimistic local update.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsRequested(true);
    }
  }, [groupId, pendingJoinGroupRequestGroupIds]);

  const onPress = () => {
    setIsLoading(true);
    joinGroupMutation.mutate(
      {
        action: isMember || isRequested ? RequestMutationAction.DELETE : RequestMutationAction.ADD,
        groupId,
        userId: currentUserId,
      },
      {
        onSuccess: (status, { action }) => {
          if (action === RequestMutationAction.DELETE) {
            setIsMember(false);
            setIsRequested(false);
            return;
          }

          if (status === RequestStatus.ACCEPTED) {
            setIsMember(true);
          }
          if (status === RequestStatus.PENDING) {
            setIsRequested(true);
          }
        },
        onError: (error) => errorAlert(error, { onAction: onPress }),
        onSettled: () => setIsLoading(false),
      }
    );
  };

  const status = isMember
    ? RequestStatus.ACCEPTED
    : isRequested
      ? RequestStatus.PENDING
      : RequestStatus.UNDETERMINED;

  const text = { accepted: 'Joined', pending: 'Requested', undetermined: 'Join' };
  const mutationPending = isLoading || joinGroupMutation.isPending;

  return variant === 'large' ? (
    <WhiteButtonWithStatus
      onPress={onPress}
      status={status}
      text={text}
      loading={mutationPending}
      disabled={mutationPending}
      compact
      uppercase={false}
      {...props}
    />
  ) : (
    <ButtonWithStatus
      onPress={onPress}
      status={status}
      text={text}
      loading={mutationPending}
      disabled={mutationPending}
      compact
      uppercase={false}
      {...props}
    />
  );
}
